/* Copyright (c) 2020-present CloudIO Inc */

import {
  FieldType,
  getFilterDataType,
  IoDatasourceAttributes,
  NormalizedAttributeType,
} from '@cloudio-saas/datasource-types';
import { useStateXSelector, useStateXValue } from '@cloudio-saas/statex';
import { FormControl, FormHelperText } from '@mui/material';
import {
  AliasAndEmbeddedPageIdValue,
  ComboAttributeChooserProp,
  ViewAttributeChooserProp,
} from 'componentregistry';
import {
  Option,
  VirtualizedMultiSelectInput,
  VirtualizedSelectInput,
} from 'custom/SelectInput';
import { designPath, pageAtom } from 'designer/atoms';
import { helpTextSelector } from 'designer/state';
import React, { memo, useEffect, useState } from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import {
  attributesPath,
  useAttributesInitialized,
} from 'store/DSAttributesCache';
import { useIoDatasourceAttributesStore } from 'store/utils/utils';
import { isEmpty, isNotEmpty } from 'utils/isEmpty';
import WaveDots from 'utils/WaveDots';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

interface AttributeOption extends Option {
  value: string;
  label: string;
  dataType: NormalizedAttributeType;
}

export type OnChangeHandler<T extends Option = Option> = (
  val: string,
  option: T | null,
) => void;

export type MultiOnChangeHandler<T extends Option = Option> = (
  val: string[],
  option: T[],
) => void;

export function AttributeChooser({
  ds,
  appUid,
  helpText,
  label,
  onChange,
  required,
  testid,
  types = 'ALL',
  value,
  autoFocus = false,
  dsAppUid,
  disabled = false,
}: {
  autoFocus?: boolean;
  ds: string;
  appUid: string;
  helpText?: string;
  label: string;
  onChange: OnChangeHandler<AttributeOption>;
  required?: boolean;
  testid?: string;
  types: FieldType[] | 'ALL';
  value: string;
  dsAppUid: string | undefined;
  disabled?: boolean;
}) {
  const store = useIoDatasourceAttributesStore(appUid);
  const attributes = useStateXValue<IoDatasourceAttributes[]>(
    attributesPath(appUid, ds),
    [],
  );
  const attributesInitialized = useAttributesInitialized(appUid, ds);
  useEffect(() => {
    store.getAttributesForDataSource(appUid, ds, dsAppUid);
  }, [appUid, ds, dsAppUid, store]);

  // leave the invalid attribute value as is so that it can be fixed in the property panel
  // and it will display the incorrect attribute value below the attribute chooser

  // useEffect(() => {
  //   if (value && store && attributes.length) {
  //     attributes.every((option, index) => {
  //       if (value === option.attributeCode) {
  //         return false;
  //       }
  //       if (index === attributes.length - 1) {
  //         onChange('', null);
  //         return false;
  //       }
  //       return true;
  //     });
  //   }
  // }, [attributes, onChange, store, value]);

  const handleChange: OnChangeHandler = (attr, option) => {
    if (attr !== value) {
      onChange(attr, option as AttributeOption);
    }
  };

  const options = attributes
    .filter((attr) => {
      let { attributeType } = attr;
      if (attr.attributeType === 'Reference') {
        attributeType = attr.refMd?.attributeType ?? 'Reference';
      }
      return types === 'ALL' || types.includes(attributeType);
    })
    .map((attr) => {
      const { attributeCode, attributeName } = attr;
      return {
        value: attributeCode,
        label: attributeName,
        dataType: getFilterDataType(attr),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label)) as AttributeOption[];

  return (
    <VirtualizedSelectInput
      data-testid={testid}
      variant="standard"
      autoFocus={autoFocus}
      fullWidth
      value={value}
      options={options}
      onChange={handleChange}
      label={label}
      startIcon={!attributesInitialized ? <WaveDots /> : null}
      disabled={!attributesInitialized || disabled}
      placeholder={
        !attributesInitialized ? 'Loading ...' : 'Choose an attribute'
      }
      required={required}
      helpText={helpText}
    />
  );
}

export function MultiAttributeChooser({
  ds,
  appUid,
  helpText,
  label,
  onChange,
  required,
  testid,
  types = 'ALL',
  value,
  dsAppUid,
}: {
  ds: string;
  appUid: string;
  helpText?: string;
  label: string;
  onChange: MultiOnChangeHandler<AttributeOption>;
  required?: boolean;
  testid: string;
  types: FieldType[] | 'ALL';
  value: string[];
  dsAppUid: string | undefined;
}) {
  const store = useIoDatasourceAttributesStore(appUid);
  const attributes = useStateXValue<IoDatasourceAttributes[]>(
    attributesPath(appUid, ds),
    [],
  );
  const attributesInitialized = useAttributesInitialized(appUid, ds);
  useEffect(() => {
    store.getAttributesForDataSource(appUid, ds, dsAppUid);
  }, [appUid, ds, dsAppUid, store]);

  // useEffect(() => {
  //   if (value && store && attributes.length) {
  //     attributes.every((option, index) => {
  //       if (value === option.attributeCode) {
  //         return false;
  //       }
  //       if (index === attributes.length - 1) {
  //         onChange([], []);
  //         return false;
  //       }
  //       return true;
  //     });
  //   }
  // }, [attributes, onChange, store, value]);

  const handleChange: MultiOnChangeHandler = (attr, option) => {
    if (attr !== value) {
      onChange(attr, option as AttributeOption[]);
    }
  };

  const options = attributes
    .filter((attr) => {
      return types === 'ALL' || types.includes(attr.attributeType);
    })
    .map(({ attributeCode, attributeName, attributeType }) => ({
      value: attributeCode,
      label: attributeName,
      dataType: attributeType,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)) as AttributeOption[];

  return (
    <FormControl
      data-testid={testid}
      required={required}
      fullWidth
      variant="standard"
      component="fieldset"
    >
      <VirtualizedMultiSelectInput
        variant="standard"
        value={value}
        options={options}
        onChange={handleChange}
        label={label}
        startIcon={!attributesInitialized ? <WaveDots /> : null}
        disabled={!attributesInitialized}
        placeholder={
          !attributesInitialized ? 'Loading ...' : 'Choose an attribute'
        }
        required={required}
      />
      <FormHelperText>{helpText}</FormHelperText>
    </FormControl>
  );
}

function AttributeChooserProperty({
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  alias,
  aliasPageId,
  onChange,
  appUid,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<
  ViewAttributeChooserProp | ComboAttributeChooserProp,
  string
> & {
  aliasPageId: string;
}) {
  const store = isNotEmpty(alias)
    ? platform.getStore(appUid, aliasPageId, alias)
    : undefined;

  let helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange: OnChangeHandler<AttributeOption> = (
    attr,
    option: AttributeOption | null,
  ) => {
    if (attr !== value) {
      onChange(property, attr);
    }
  };
  const page = platform.getPage(appUid, pageId);
  const itemWithDefaults = itemWithDefaultValues(page[activeItemId]);
  const types = def.getAttributeDataTypes(itemWithDefaults);

  if (!store) {
    return <>Missing Data Store Reference!</>;
  }
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  if (isEmpty(helpText) && Array.isArray(types)) {
    const typesString = types.join(', ');
    helpText = `Allowed types: ${typesString}`;
  }

  return (
    <AttributeChooser
      disabled={isViewOnlyUser}
      ds={store.ds}
      appUid={appUid}
      helpText={helpText}
      label={def.label}
      onChange={handleChange}
      required={required}
      testid={property}
      types={types}
      value={value}
      dsAppUid={store.dsAppUid}
    />
  );
}

function ViewAttributeChooserProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  hmr,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<ViewAttributeChooserProp, string>) {
  const { aliasType, getAlias } = def;
  let aliasAndEmbeddedPageIdValue: AliasAndEmbeddedPageIdValue | null = null;
  if (getAlias) {
    const page = platform.getter(pageAtom, { params: { appUid, pageId } });
    if (page) {
      const item = itemWithDefaultValues(page[activeItemId]);
      aliasAndEmbeddedPageIdValue = getAlias(page, item, {
        pageId,
        appUid,
        itemId: activeItemId,
        mgr,
        platform,
      });
    }
  } else {
    const _aliasType =
      typeof aliasType === 'function'
        ? aliasType({ mgr, appUid, pageId, itemId: activeItemId, platform })
        : aliasType;
    aliasAndEmbeddedPageIdValue = platform.getDataSourceAlias(
      appUid,
      pageId,
      activeItemId,
      _aliasType,
    );
  }
  const [, setState] = useState([]);
  useEffect(() => {
    if (def.attributeRefreshCallback) {
      def.attributeRefreshCallback(() => {
        setState([]);
      });
    }
  }, [def]);

  return (
    <AttributeChooserProperty
      isViewOnlyUser={isViewOnlyUser}
      appUid={appUid}
      hmr={hmr}
      activeItemId={activeItemId}
      def={def}
      key={`${property}-${activeItemId}`}
      pageId={pageId}
      property={property}
      mgr={mgr}
      value={value as string}
      alias={aliasAndEmbeddedPageIdValue?.alias}
      aliasPageId={aliasAndEmbeddedPageIdValue?.embeddedPageId ?? pageId}
      onChange={onChange}
      platform={platform}
    />
  );
}

export function ComboAttributeChooserProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  hmr,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<ComboAttributeChooserProp, string>) {
  const aliasType =
    typeof def.aliasType === 'function'
      ? def.aliasType({ mgr, appUid, pageId, itemId: activeItemId, platform })
      : def.aliasType;
  const aliasAndEmbeddedPageIdProps = mgr.getDataSourceAliasProperty(
    aliasType,
    { mgr, appUid, pageId, itemId: activeItemId, platform },
  );
  let aliasPageId = useStateXValue(
    designPath({
      appUid,
      pageId,
      itemId: activeItemId,
      key: 'props',
      subkey:
        aliasAndEmbeddedPageIdProps?.embeddedPageIdProp ??
        '_no_embedded_page_id_',
    }),
    '',
  );
  if (isEmpty(aliasPageId)) {
    // not embedded, hence use the current pageId
    aliasPageId = pageId;
  }
  const alias = useStateXValue(
    designPath({
      appUid,
      pageId,
      itemId: activeItemId,
      key: 'props',
      subkey: aliasAndEmbeddedPageIdProps?.aliasProp,
    }),
    '',
  );
  const [, setState] = useState([]);
  useEffect(() => {
    if (def.attributeRefreshCallback) {
      def.attributeRefreshCallback(() => {
        setState([]);
      });
    }
  }, [def]);

  return (
    <AttributeChooserProperty
      isViewOnlyUser={isViewOnlyUser}
      appUid={appUid}
      hmr={hmr}
      activeItemId={activeItemId}
      def={def}
      key={`${property}-${activeItemId}`}
      pageId={pageId}
      property={property}
      mgr={mgr}
      value={value as string}
      aliasPageId={aliasPageId}
      alias={alias}
      onChange={onChange}
      platform={platform}
    />
  );
}

export default memo(ViewAttributeChooserProperty);
